import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Clock,
  FileArchive,
  FileText,
  Image as ImageIcon,
  KeyRound,
  Link2,
  Loader2,
  Lock,
  PackageCheck,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";

const inp =
  "h-10 w-full rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";
const ta =
  "w-full rounded-lg border border-line p-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";

const fmtSize = (b) =>
  !b ? "" : b >= 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b >= 1e6 ? `${(b / 1e6).toFixed(2)} Mo` : `${Math.max(1, Math.round(b / 1e3))} Ko`;

const LIMIT_PRESETS = [0, 1, 2, 3, 5, 10, 20];
const todayPlus = (days) => {
  const t = new Date();
  t.setDate(t.getDate() + days);
  return t.toISOString().slice(0, 10);
};

const TYPES = [
  { value: "file", label: "Fichier", Icon: FileArchive },
  { value: "link", label: "Lien", Icon: Link2 },
  { value: "key", label: "Clé de licence", Icon: KeyRound },
  { value: "text", label: "Instructions / Texte", Icon: FileText },
  { value: "image", label: "Image", Icon: ImageIcon },
];
const META = Object.fromEntries(TYPES.map((t) => [t.value, t]));

const EMPTY_ITEM = {
  id: "", type: "file", title: "", url: "", value: "", content: "", description: "",
  file_path: "", file_name: "", file_size: 0, is_hidden: false, expires_at: "", download_limit: 0,
};

function ItemEditor({ item, deliveryId, base, onSave, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY_ITEM, ...(item || {}) });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isFile = form.type === "file" || form.type === "image";

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`${base}/${deliveryId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, file_path: data.path, file_name: data.name, file_size: data.size, title: f.title || data.name }));
      toast.success("Fichier téléversé");
    } catch (e) {
      toast.error(apiError(e, "Upload impossible"));
    } finally {
      setUploading(false);
    }
  };

  const save = () => {
    if (!form.title.trim()) return toast.error("Nom requis");
    if (form.type === "link" && !form.url.trim()) return toast.error("URL requise");
    if (form.type === "key" && !form.value.trim()) return toast.error("Clé requise");
    if (form.type === "text" && !form.content.trim()) return toast.error("Texte requis");
    if (isFile && !form.file_path) return toast.error("Importez un fichier");
    onSave({ ...form, download_limit: Number(form.download_limit) || 0 });
  };

  return (
    <div className="space-y-3 rounded-xl border border-brand/30 bg-brand-soft/30 p-4" data-testid="dm-item-editor">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {TYPES.map((opt) => (
          <button
            key={opt.value}
            type="button"
            data-testid={`dm-type-${opt.value}`}
            onClick={() => setForm((f) => ({ ...f, type: opt.value }))}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-[11px] font-bold transition-colors duration-200 ${
              form.type === opt.value ? "border-brand bg-brand-light text-brand" : "border-line text-slate-600 hover:border-brand/40"
            }`}
          >
            <opt.Icon className="h-3.5 w-3.5 shrink-0" /> {opt.label}
          </button>
        ))}
      </div>

      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Nom du contenu *</span>
        <input data-testid="dm-item-title" value={form.title} onChange={set("title")} className={inp}
               placeholder={form.type === "link" ? "Téléchargement principal" : "Ex : Setup.exe"} />
      </label>

      {form.type === "link" && (
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Lien de téléchargement *</span>
          <input data-testid="dm-item-url" value={form.url} onChange={set("url")} className={inp} placeholder="https://exemple.com/telechargement" />
        </label>
      )}
      {form.type === "key" && (
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Clé *</span>
          <input data-testid="dm-item-value" value={form.value} onChange={set("value")} className={`${inp} font-mono`} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" />
        </label>
      )}
      {form.type === "text" && (
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Instructions / Texte *</span>
          <textarea data-testid="dm-item-content" value={form.content} onChange={set("content")} rows={4} className={ta}
                    placeholder="Installez le logiciel puis utilisez la clé fournie…" />
        </label>
      )}
      {isFile && (
        <div>
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">{form.type === "image" ? "Image" : "Fichier"} *</span>
          <input ref={fileRef} type="file" className="hidden" data-testid="dm-item-file-input"
                 accept={form.type === "image" ? "image/*" : undefined}
                 onChange={(e) => upload(e.target.files?.[0])} />
          {form.file_path ? (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="dm-item-file-ready">
              <FileArchive className="h-5 w-5 shrink-0 text-brand" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-ink">{form.file_name}</p>
                <p className="text-[11px] text-slate-500">{fmtSize(form.file_size)}</p>
              </div>
              <button type="button" onClick={() => fileRef.current?.click()} className="text-xs font-bold text-brand hover:underline">Remplacer</button>
            </div>
          ) : (
            <button type="button" data-testid="dm-item-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line px-4 py-5 text-xs font-bold text-slate-500 hover:border-brand hover:text-brand">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Téléversement…" : "Importer depuis mon ordinateur"}
            </button>
          )}
        </div>
      )}

      <label className="block">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Description du fichier (facultatif)</span>
        <input data-testid="dm-item-desc" value={form.description} onChange={set("description")} className={inp} placeholder="Courte description visible par le client" />
      </label>

      <div className="rounded-xl border border-line bg-white p-3">
        <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
          <Clock className="h-3.5 w-3.5" /> Accès & limites (visibles par le client)
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold text-slate-500">Durée d'accès</span>
            <select
              data-testid="dm-item-access"
              value={form.expires_at ? "date" : "permanent"}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value === "permanent" ? "" : f.expires_at || todayPlus(30) }))}
              className={inp}
            >
              <option value="permanent">Permanent (aucune expiration)</option>
              <option value="date">Expire à une date…</option>
            </select>
            {form.expires_at ? (
              <input data-testid="dm-item-expires" type="date" min={todayPlus(0)} value={form.expires_at} onChange={set("expires_at")} className={`${inp} mt-2`} />
            ) : null}
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold text-slate-500">Limite de téléchargements</span>
            <select
              data-testid="dm-item-limit"
              value={LIMIT_PRESETS.includes(Number(form.download_limit)) ? String(Number(form.download_limit) || 0) : "custom"}
              onChange={(e) => setForm((f) => ({ ...f, download_limit: e.target.value === "custom" ? Number(f.download_limit) || 1 : Number(e.target.value) }))}
              className={inp}
            >
              <option value="0">Illimité</option>
              <option value="1">1 téléchargement</option>
              <option value="2">2 téléchargements</option>
              <option value="3">3 téléchargements</option>
              <option value="5">5 téléchargements</option>
              <option value="10">10 téléchargements</option>
              <option value="20">20 téléchargements</option>
              <option value="custom">Personnalisé…</option>
            </select>
            {!LIMIT_PRESETS.includes(Number(form.download_limit)) && (
              <input data-testid="dm-item-limit-custom" type="number" min="1" value={form.download_limit} onChange={set("download_limit")} className={`${inp} mt-2`} placeholder="Nombre de téléchargements" />
            )}
          </label>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          La limite de téléchargements s'applique aux fichiers. Après la date d'expiration, le fichier, lien ou clé n'est plus accessible au client.
        </p>
      </div>

      <div className="flex justify-end gap-2 border-t border-line/60 pt-3">
        <button type="button" onClick={onCancel} className="rounded-lg border border-line px-4 py-2 text-sm font-bold text-slate-600 hover:text-ink">Annuler</button>
        <button type="button" data-testid="dm-item-save" onClick={save} className="rounded-lg bg-brand px-5 py-2 text-sm font-bold text-white hover:bg-brand/90">Enregistrer le contenu</button>
      </div>
    </div>
  );
}

export default function DownloadsManager({ productId, scope = "admin" }) {
  const base = scope === "vendor" ? "/vendor/product-deliveries" : "/admin/product-deliveries";
  const [loading, setLoading] = useState(true);
  const [delivery, setDelivery] = useState(null);
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState({ version: "", instructions: "", important_info: "", custom_message: "" });
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [editor, setEditor] = useState(null);

  const applyDoc = (data) => {
    setDelivery(data);
    setItems(data.items || []);
    setFields({
      version: data.version || "", instructions: data.instructions || "",
      important_info: data.important_info || "", custom_message: data.custom_message || "",
    });
    setStatus(data.status === "disabled" ? "disabled" : "active");
  };

  const load = useCallback(async () => {
    if (!productId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`${base}/by-product/${productId}`);
      if (data && data.id) applyDoc(data);
      else setDelivery(null);
    } catch {
      setDelivery(null);
    } finally {
      setLoading(false);
    }
  }, [productId, base]);

  useEffect(() => { load(); }, [load]);

  const enable = async () => {
    setEnabling(true);
    try {
      const { data } = await api.post(base, { product_id: productId });
      applyDoc(data);
      toast.success("Téléchargements activés — ajoutez vos fichiers, liens et instructions");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEnabling(false);
    }
  };

  const saveItem = (item) => {
    const withId = item.id ? item : { ...item, id: crypto.randomUUID() };
    setItems((list) => {
      const idx = list.findIndex((i) => i.id === withId.id);
      if (idx >= 0) { const n = [...list]; n[idx] = withId; return n; }
      return [...list, withId];
    });
    setEditor(null);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.put(`${base}/${delivery.id}/content`, { items });
      await api.put(`${base}/${delivery.id}/settings`, {
        version: fields.version, instructions: fields.instructions, important_info: fields.important_info,
        custom_message: fields.custom_message, default_access: "permanent", default_expires_at: "",
        default_download_limit: 0, auto_activate: true,
      });
      await api.put(`${base}/${delivery.id}/status`, { status });
      toast.success("Fichiers et téléchargements enregistrés");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setF = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));

  if (!productId) {
    return (
      <div className="rounded-2xl border border-dashed border-line bg-surface p-5 text-center" data-testid="downloads-manager">
        <FileArchive className="mx-auto h-6 w-6 text-slate-300" />
        <p className="mt-2 text-xs font-semibold text-slate-500">
          Enregistrez d'abord le produit pour préparer ses fichiers et téléchargements.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-line bg-white p-5" data-testid="downloads-manager">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-display text-base font-bold text-ink">
            <PackageCheck className="h-4 w-4 text-brand" /> Fichiers et téléchargements
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Préparez à l'avance ce que le client recevra (logiciel, jeu, fichier numérique). Débloqué automatiquement après validation du paiement.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
      ) : !delivery ? (
        <div className="rounded-xl border border-dashed border-line bg-surface p-6 text-center">
          <Lock className="mx-auto h-6 w-6 text-slate-300" />
          <p className="mt-2 text-sm font-bold text-ink">Ce produit nécessite-t-il un téléchargement ?</p>
          <p className="mt-1 text-xs text-slate-500">Activez pour ajouter fichiers, liens, clés, instructions et version.</p>
          <button type="button" data-testid="dm-enable" onClick={enable} disabled={enabling}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand/90 disabled:opacity-60">
            {enabling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Activer les téléchargements
          </button>
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Version (logiciel / jeu)</span>
              <input data-testid="dm-version" value={fields.version} onChange={setF("version")} className={inp} placeholder="Ex : 2.5" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Statut</span>
              <select data-testid="dm-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inp}>
                <option value="active">Actif (téléchargements disponibles après validation)</option>
                <option value="disabled">Désactivé</option>
              </select>
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Instructions de téléchargement / installation</span>
            <textarea data-testid="dm-instructions" value={fields.instructions} onChange={setF("instructions")} rows={3} className={ta}
                      placeholder="Ex : Décompressez l'archive, lancez Setup.exe puis entrez la clé…" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Informations importantes</span>
            <textarea data-testid="dm-important" value={fields.important_info} onChange={setF("important_info")} rows={2} className={ta}
                      placeholder="Ex : Compatible Windows 10/11 64 bits uniquement." />
          </label>

          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-ink">Contenus ({items.length})</p>
            {!editor && (
              <button type="button" data-testid="dm-add-item" onClick={() => setEditor({})}
                      className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand/90">
                <Plus className="h-3.5 w-3.5" /> Ajouter un fichier / lien / clé
              </button>
            )}
          </div>

          {editor !== null && (
            <ItemEditor item={editor.id ? editor : null} deliveryId={delivery.id} base={base}
                        onSave={saveItem} onCancel={() => setEditor(null)} />
          )}

          {items.length === 0 && editor === null ? (
            <p className="rounded-xl border border-dashed border-line py-6 text-center text-xs text-slate-400" data-testid="dm-empty">
              Aucun contenu — ajoutez fichiers, liens, clés ou instructions.
            </p>
          ) : (
            <div className="space-y-2" data-testid="dm-items">
              {items.map((item, index) => {
                const Icon = (META[item.type] || META.text).Icon;
                return (
                  <div key={item.id || index} className="flex flex-wrap items-center gap-2.5 rounded-lg border border-line bg-white p-2.5" data-testid={`dm-item-${index}`}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand"><Icon className="h-3.5 w-3.5" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-bold text-ink">{item.title}</p>
                      <p className="truncate text-[11px] text-slate-400">
                        {(META[item.type] || {}).label}
                        {item.file_name ? ` · ${item.file_name} · ${fmtSize(item.file_size)}` : item.url ? ` · ${item.url}` : item.type === "key" ? " · ••••" : ""}
                      </p>
                      {(item.expires_at || Number(item.download_limit) > 0) && (
                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`dm-item-limits-${index}`}>
                          {item.expires_at && (
                            <span className="flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                              <Clock className="h-2.5 w-2.5" /> Expire le {item.expires_at}
                            </span>
                          )}
                          {Number(item.download_limit) > 0 && (
                            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                              Limite : {item.download_limit} tél.
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <button type="button" data-testid={`dm-edit-${index}`} onClick={() => setEditor(item)}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-brand"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" data-testid={`dm-delete-${index}`} onClick={() => setItems((l) => l.filter((_, i) => i !== index))}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end border-t border-line pt-4">
            <button type="button" data-testid="dm-save-all" onClick={saveAll} disabled={saving}
                    className="flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand/90 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />}
              Enregistrer les téléchargements
            </button>
          </div>
        </>
      )}
    </div>
  );
}
